import { createTokenProvider } from './createTokenProvider';
import { useState, useEffect } from 'react';

/**
 * Handles tokens for logging in, logging off, and checking whether authenticated
 */
const createAuthProvider = () => {
  const tokenProvider = createTokenProvider();

  /**
   * @param newToken Token received when user logs in
   */
  const login: typeof tokenProvider.setToken = (newToken) => {
    localStorage.removeItem('profile');
    tokenProvider.setToken(newToken);
  };

  /**
   * TODO: send something to backend after logging out?
   */
  const logout = () => {
    localStorage.removeItem('profile');
    tokenProvider.setToken(null);
  };

  /**
   * @returns string User token for authentication 
   */
  const getToken = () => {
    return tokenProvider.getToken();
  };

  const useAuth = () => {
    const [isLogged, setIsLogged] = useState(tokenProvider.isLoggedIn());
    useEffect(() => {
      const listener = (newIsLogged: boolean) => {
        setIsLogged(newIsLogged);
      };
      tokenProvider.subscribe(listener);
      return () => {
        tokenProvider.unsubscribe(listener);
      };
    }, []);
    return { isLogged } as { isLogged: typeof isLogged };
  };

  return {
    useAuth,
    getToken,
    login,
    logout,
  };
};

export const {
  useAuth,
  getToken,
  login,
  logout,
} = createAuthProvider();

/**
 * Bridge between the (non-React) axios layer and the React <AuthModal />.
 *
 * When a request comes back unauthorized (returnCode 401) the request layer
 * calls `requestAuth()`. The <AuthModal /> mounted at the app root registers a
 * handler through `setAuthHandler` and resolves the returned promise with
 * `true` once the user logs in again (or `false` if they cancel).
 *
 * Calls are single-flighted: while one re-login prompt is open every other
 * 401 awaits the same promise instead of stacking up multiple modals.
 */
type AuthHandler = () => Promise<boolean>;

let authHandler: AuthHandler | null = null;
let pendingAuthPromise: Promise<boolean> | null = null;

/**
 * Registered by <AuthModal /> on mount.
 */
export const setAuthHandler = (newHandler: AuthHandler | null) => {
  authHandler = newHandler;
};

/**
 * Ask the user to re-login without leaving the current page.
 *
 * @returns true if the user logged in again, false if they cancelled or no
 *          modal is mounted (falls back to redirecting to the login page).
 */
export const requestAuth = (): Promise<boolean> => {
  if (!authHandler) {
    // No modal mounted (e.g. the login page itself) -> fall back to redirect.
    logout();
    window.history.pushState({ returnCode: 401 }, '', '/auth/login');
    window.location.assign('/auth/login');
    return Promise.resolve(false);
  }

  if (!pendingAuthPromise) {
    pendingAuthPromise = authHandler().finally(() => {
      pendingAuthPromise = null;
    });
  }

  return pendingAuthPromise;
};
